import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  getRequiredObjectId,
  selectSiteAssignmentAndTransform,
  SiteAssignmentBuilder,
  SiteAssignmentEntity,
  SiteAssignmentSiteAssocEntity,
  SiteAssignmentType,
  TransportCategory,
  useCreateSiteAssignmentMutation,
  useGetSiteAssignmentQuery,
  useUpdateSiteAssignmentMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {v4 as uuid} from 'uuid';
import {SiteSiteAssignmentFormValues} from "../../components/form/site-assignment/SiteSiteAssignmentForm/form-context";
import {
  SiteSiteAssignmentForm
} from "../../components/form/site-assignment/SiteSiteAssignmentForm/SiteSiteAssignmentForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {Result} from "../../components/result/Result";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

type Props = {
  id?: string;
  siteId: string;
  type: SiteAssignmentType;
  category: TransportCategory;
  copy?: boolean;
  formDefaults?: Partial<SiteSiteAssignmentFormValues>
}

export const SiteSiteAssignmentFormPageRoute = () => {
  const {state} = useLocation();
  const navigate = useNavigate()

  const id = state?.id;
  const copy = state?.copy;
  const siteId = state?.siteId
  const type = state?.type
  const category = state?.category
  const formDefaults = state?.formDefaults;

  if (!siteId || !type || !category) {
    navigate(-1);
    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Result title={"Error"} message={"Site, Type, or Category Undefined."}/>
          </Container>
        )}
      </ThemedPage>
    );
  }

  return (
    <SiteSiteAssignmentFormPage
      id={id}
      copy={copy}
      siteId={siteId}
      type={type}
      category={category}
      formDefaults={formDefaults}/>
  );
};


export const toFormDefaults = (assignment: SiteAssignmentEntity | undefined): Partial<SiteSiteAssignmentFormValues> => {
  return {
    strategy: assignment?.strategy,
    assocs: assignment?.siteAssocs?.map((a) => {
      return {
        site: a.siteId,
        normalRank: a.normalPreferenceRank,
        serviceRank: a.servicePreferenceRank,
      };
    })
  };
};

export const SiteSiteAssignmentFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    siteId,
    type,
    category,
    formDefaults
  } = props;

  const navigate = useNavigate();
  const {regionId} = useApp()
  const {t} = useTranslation(["translation"]);

  const {currSiteAssignment, isLoading} = useGetSiteAssignmentQuery(id ?? skipToken, {
    selectFromResult: ({data, isLoading}) => ({
      currSiteAssignment: data ? selectSiteAssignmentAndTransform(data) : undefined,
      isLoading: isLoading
    })
  });

  const _formDefaults: Partial<SiteSiteAssignmentFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaults(currSiteAssignment);
  }, [currSiteAssignment, formDefaults]);

  const titleKey: string = useMemo(() => {
    if (currSiteAssignment && !copy) {
      return "page.sitesiteassignment.form.title.edit";
    } else {
      return "page.sitesiteassignment.form.title.new";
    }
  }, [currSiteAssignment, copy]);

  const [createSiteAssignment, {isLoading: isCreating}] = useCreateSiteAssignmentMutation();
  const [updateSiteAssignment, {isLoading: isUpdating}] = useUpdateSiteAssignmentMutation();

  const onSiteAssignmentSubmit = useCallback((formValues: SiteSiteAssignmentFormValues) => {
    if (!regionId) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Region Unknown"
      })
      return
    }

    if (currSiteAssignment && !copy) {
      const siteAssocs = formValues.assocs.map((s) => {
        const existingAssoc = currSiteAssignment.siteAssocs?.find(
          (a) => a.siteId === getRequiredObjectId(s.site)
        );
        if (existingAssoc) {
          existingAssoc.normalPreferenceRank = s.normalRank;
          existingAssoc.servicePreferenceRank = s.serviceRank;
          return existingAssoc;
        } else {
          return SiteAssignmentSiteAssocEntity.create(
            currSiteAssignment.id,
            getRequiredObjectId(s.site),
            s.normalRank,
            s.serviceRank
          );
        }
      });

      const assignment = currSiteAssignment.clone();
      assignment.strategy = formValues.strategy;
      assignment.siteAssocs = siteAssocs;
      assignment.lastModifiedDttm = adjustToTimeZone(new Date()).getTime();

      updateSiteAssignment(assignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Updated",
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {
      const newId = uuid()

      const siteAssocs = formValues.assocs.map((s) => {
        return SiteAssignmentSiteAssocEntity.create(
          newId,
          getRequiredObjectId(s.site),
          s.normalRank,
          s.serviceRank
        );
      });

      const assignment = SiteAssignmentBuilder.builder(newId)
        .setStrategy(formValues.strategy)
        .setType(type)
        .setCategory(category)
        .setSite(siteId)
        .setRegion(regionId)
        .setSiteAssocs(siteAssocs)
        .build();

      createSiteAssignment(assignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Created",
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [currSiteAssignment, createSiteAssignment, updateSiteAssignment, regionId, siteId, type, category, navigate, copy]);

  if (!regionId) {
    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Result title={"Error"} message={"Region Unknown."}/>
          </Container>
        )}
      </ThemedPage>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t(titleKey)}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <SiteSiteAssignmentForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onSiteAssignmentSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

